import { useCallback } from "react"
import type { ComponentProps } from "react"
import { useDispatch } from "react-redux"
import { Creators as DialogCreators } from "modules/dialog/model"
import DashboardSearch from "../components/search"
import { DashboardIdProvider, useDashboardId } from "../context/dashboard-id"
import { SelectedInstrument } from "helpers/types/instruments/selected"
import type { InstrumentSelectionData } from "../analytics"

type SearchProps = ComponentProps<typeof DashboardSearch>

function DashboardScopedSearch({ dashboardId, ...searchProps }: SearchProps & { dashboardId: string }) {
  return (
    <DashboardIdProvider value={dashboardId}>
      <DashboardSearch {...searchProps} />
    </DashboardIdProvider>
  )
}

/**
 * Returns pre-configured callback to open the Unified Search.
 * @param setInstrument callback to set instrument
 * @param searchType "benchmark" or "base" are supported for now
 * @param selectedInstrument used to load experiments if available
 * @param disableModelSelector disable model selection inside search
 * @param includeBenchmarks include benchmarks in available instruments
 * @param onInstrumentSelection callback for tracking instrument selection events
 */
export default function useOpenUnifiedSearch(
  setInstrument: (instrument: SelectedInstrument | null) => void,
  searchType: string,
  selectedInstrument: SelectedInstrument | null = null,
  disableModelSelector = false,
  includeBenchmarks = false,
  onInstrumentSelection?: (data: InstrumentSelectionData) => void,
) {
  const dispatch = useDispatch()
  const dashboardId = useDashboardId()

  return useCallback(() => {
    dispatch(DialogCreators.open({
      content: (
        <DashboardScopedSearch
          dashboardId={dashboardId}
          close={undefined as never}
          searchType={searchType}
          setInstrument={setInstrument}
          selectedInstrument={selectedInstrument}
          disableModelSelector={disableModelSelector}
          includeBenchmarks={includeBenchmarks}
          onInstrumentSelection={onInstrumentSelection}
        />
      ),
    }))
  }, [dispatch, dashboardId, searchType, setInstrument, selectedInstrument, disableModelSelector, includeBenchmarks, onInstrumentSelection])
}
